/**
 * "Best times" in the Flux Agent memory dialog: for each kind of activity, the windows that tend
 * to suit the person as plain-language chips ("Deep work: weekday mornings"), the windows that are
 * harder, a one-line reason and a 24-hour heat strip; then the timing cues Flux Agent heard, each
 * with a button to forget it. The map is built on the server from those cues and the person's own
 * history; deleting a cue reloads the view so its effect disappears at once.
 *
 * @module apps/web/src/components/flux-agent/timing/FluxAgentBestTimes
 */

import { useCallback, useEffect, useState } from 'react';
import { Clock, Loader2, Trash2 } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  fluxAgentTimingApi,
  type FluxAgentTiming,
  type FluxAgentTimingCue,
  type FluxAgentTimingKind,
} from '@/lib/fluxAgentTimingApi';
import { TimingHeatStrip } from './TimingHeatStrip';

/** Whether a kind has anything worth showing: a window in words or a reasonably confident hour. */
export function hasSignal(kind: FluxAgentTimingKind): boolean {
  return (
    kind.best.length > 0 ||
    kind.harder.length > 0 ||
    kind.hours.some(hour => hour.confidence >= 0.2 && Math.abs(hour.value) >= 0.1)
  );
}

/** Inputs for {@link KindRow}. */
interface KindRowProps {
  /** The kind to show. */
  kind: FluxAgentTimingKind;
}

/** One kind: best and harder chips, the reason, and the heat strip.
 * @component KindRow
 */
function KindRow({ kind }: KindRowProps) {
  return (
    <li className="space-y-1.5 rounded-md border px-3 py-2" data-testid={`best-times-${kind.kind}`}>
      <div className="flex flex-wrap items-center gap-1.5">
        {kind.best.map(window => (
          <Badge key={window} variant="secondary" data-testid="best-time-chip">
            {kind.label}: {window}
          </Badge>
        ))}
        {kind.harder.map(window => (
          <Badge
            key={window}
            variant="outline"
            className="text-muted-foreground"
            data-testid="harder-time-chip"
          >
            Harder for {kind.label.toLowerCase()}: {window}
          </Badge>
        ))}
        {kind.best.length === 0 && kind.harder.length === 0 && (
          <span className="text-sm font-medium">{kind.label}</span>
        )}
      </div>
      {kind.why && <p className="text-muted-foreground text-xs">{kind.why}</p>}
      <TimingHeatStrip hours={kind.hours} label={kind.label} />
    </li>
  );
}

/** Inputs for {@link CueRow}. */
interface CueRowProps {
  /** The stored cue. */
  cue: FluxAgentTimingCue;
  /** True while this cue is being deleted. */
  busy: boolean;
  /** Called to forget the cue. */
  onDelete: () => void;
}

/** One stored cue with its windows, paraphrase and a forget button.
 * @component CueRow
 */
function CueRow({ cue, busy, onDelete }: CueRowProps) {
  const direction = cue.suitability >= 0 ? 'Good for' : 'Harder for';
  return (
    <li
      className="flex items-start justify-between gap-2 rounded-md border px-3 py-2 text-sm"
      data-testid="timing-cue"
    >
      <div className="min-w-0">
        <p className="font-medium">
          {direction} {cue.label.toLowerCase()} · <span className="font-normal">{cue.windows}</span>
        </p>
        {cue.basis && <p className="text-muted-foreground text-xs">{cue.basis}</p>}
      </div>
      <Button
        type="button"
        variant="ghost"
        size="sm"
        className="shrink-0"
        disabled={busy}
        aria-label={`Forget: ${direction.toLowerCase()} ${cue.label.toLowerCase()}, ${cue.windows}`}
        onClick={onDelete}
      >
        {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Trash2 className="h-4 w-4" />}
      </Button>
    </li>
  );
}

/** Load and show the caller's best times and timing cues, with per-cue delete. */
export function FluxAgentBestTimes() {
  const [timing, setTiming] = useState<FluxAgentTiming | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [deleting, setDeleting] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    const result = await fluxAgentTimingApi.getTiming();
    setLoading(false);
    if (!result.ok) {
      setError(result.message);
      return;
    }
    setError(null);
    setTiming(result.data);
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const forget = async (cue: FluxAgentTimingCue) => {
    setDeleting(cue.id);
    const result = await fluxAgentTimingApi.deleteCue(cue.id);
    setDeleting(null);
    if (!result.ok) {
      setError(result.message);
      return;
    }
    await load();
  };

  if (loading && !timing) {
    return (
      <p className="text-muted-foreground flex items-center gap-2 text-xs">
        <Loader2 className="h-3.5 w-3.5 animate-spin" /> Loading…
      </p>
    );
  }

  const kinds = timing?.kinds.filter(hasSignal) ?? [];
  const cues = timing?.cues ?? [];
  return (
    <div className="space-y-4" data-testid="best-times">
      <p className="text-muted-foreground flex items-start gap-2 text-sm">
        <Clock className="mt-0.5 h-4 w-4 shrink-0" />
        When each kind of activity tends to suit you, from what you told Flux Agent and how your
        days have gone. Fainter means less evidence.
      </p>
      {error && (
        <p
          role="alert"
          className="border-destructive/40 bg-destructive/5 text-destructive rounded-md border px-3 py-2 text-xs"
        >
          {error}
        </p>
      )}
      {timing && !timing.learningEnabled && cues.length > 0 && (
        <p className="text-muted-foreground rounded-md border px-3 py-2 text-xs">
          Learning from conversations is off, so the cues below don’t shape these times.
        </p>
      )}
      {kinds.length === 0 ? (
        <p className="text-muted-foreground text-sm">
          Nothing yet. Mention when you do your best work, or when calls or errands suit you, and
          Flux Agent will remember it here.
        </p>
      ) : (
        <ul className="space-y-2" aria-label="Best times by kind of activity">
          {kinds.map(kind => (
            <KindRow key={kind.kind} kind={kind} />
          ))}
        </ul>
      )}
      {cues.length > 0 && (
        <section className="space-y-1.5" aria-label="What you told Flux Agent">
          <h3 className="text-sm font-medium">What you told Flux Agent</h3>
          <ul className="space-y-1.5">
            {cues.map(cue => (
              <CueRow
                key={cue.id}
                cue={cue}
                busy={deleting === cue.id}
                onDelete={() => void forget(cue)}
              />
            ))}
          </ul>
        </section>
      )}
    </div>
  );
}
